import { Checkbox } from "@/src/components/checkbox";
import { FileIcon } from "@/src/components/FileIcon";
import Loading from "@/src/components/Loading";
import { useAllDataRoomFoldersBrowseQuery } from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import {
  buildFolderHierarchy,
  FolderWrapper,
} from "@/src/utils/buildFolderHierarchy";
import { FolderIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import { ChevronDownIcon, ChevronRightIcon } from "lucide-react";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { DataRoomContext } from "..";
import { getDataRoomIndex } from "@/src/utils/getDataRoomIndex";
import { usePrefetchDataRoomFolderBrowse } from "@/src/hooks/usePrefetchDataRoomFolderBrowse";
import { usePrefetchDataRoomFileShell } from "@/src/hooks/usePrefetchDataRoomFileShell";
import { FileType } from "@/src/graphql/generated";
import {
  ExternalDropTarget,
  useExternalFileDrop,
} from "../dnd/useExternalFileDrop";
import { useDataRoomExternalUpload } from "../dnd/useDataRoomExternalUpload";
import { PermissionSeed } from "@/src/components/data_room/file_uploaders/parseDataTransferFiles";
import { classNames } from "@/src/utils/cn";

interface TreeViewProps {
  folderId: string;
  currentFolderName: string;
  hasWriteAccess: boolean;
  currentFolderPermission: PermissionSeed | null;
}

export function TreeView(props: TreeViewProps) {
  const client = useGqlClient();
  const { dealId } = useParams<{ dealId: string }>();
  const allFolders = useAllDataRoomFoldersBrowseQuery(
    client,
    {
      dealId,
    },
    {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  );

  const { indexingEnabled, filters } = useContext(DataRoomContext);
  const { handleExternalDrop } = useDataRoomExternalUpload();

  const surfaceTarget = useMemo<ExternalDropTarget>(
    () => ({
      folderId: props.folderId,
      folderName: props.currentFolderName,
      permission: props.currentFolderPermission,
    }),
    [
      props.folderId,
      props.currentFolderName,
      props.currentFolderPermission?.id,
      props.currentFolderPermission?.name,
      props.currentFolderPermission?.description,
    ],
  );

  const {
    isSurfaceDragOver,
    registerFolderTarget,
    surfaceHandlers,
  } = useExternalFileDrop({
    enabled: props.hasWriteAccess,
    surfaceTarget,
    onDrop: ({ files, folders, target }) => {
      handleExternalDrop({
        files,
        folders,
        target,
        allowAutoUpload: true,
      });
    },
  });

  const filteredFolders = useMemo(() => {
    if (!allFolders.data) return [];
    return allFolders.data.deal.dataRoom.allFolders.filter((folder) => {
      if (folder.parentFolders.length === 0) {
        return true;
      }

      if (filters.createdBy && folder.createdBy.id !== filters.createdBy.id) {
        return false;
      }

      if (filters.lastUpdated && folder.updatedAt < filters.lastUpdated) {
        return false;
      }

      return true;
    });
  }, [allFolders.data, filters]);

  const folderHierarchy = useMemo(
    () =>
      buildFolderHierarchy(
        filteredFolders as Parameters<typeof buildFolderHierarchy>[0],
        indexingEnabled,
        [],
        [],
        filters,
      ),
    [filteredFolders, indexingEnabled, filters],
  );

  if (allFolders.error) {
    return (
      <div>
        <p className="font-semibold text-gray-500">Something went wrong</p>
      </div>
    );
  }

  if (allFolders.isLoading || !allFolders.data) {
    return (
      <div className="h-[calc(100vh-300px)] flex justify-center items-center">
        <Loading />
      </div>
    );
  }

  return (
    <div
      className={classNames(
        "rounded-md border-2 border-dashed transition-colors min-h-[200px]",
        isSurfaceDragOver
          ? "border-persian-500 bg-persian-50/40"
          : "border-transparent",
      )}
      {...surfaceHandlers}
    >
      {folderHierarchy.map((folder) => {
        return (
          <FolderContent
            key={folder.folder.id}
            folder={folder}
            searchTerm={""}
            mode={"latest"}
            depth={0}
            registerExternalFolderTarget={registerFolderTarget}
            fallbackPermission={props.currentFolderPermission}
          />
        );
      })}
    </div>
  );
}

export function FolderContent(props: {
  folder: FolderWrapper;
  searchTerm: string;
  mode: "latest" | "versions";
  depth: number;
  showChildFiles?: boolean;
  showChevron?: boolean;
  showCheckbox?: boolean;
  registerExternalFolderTarget?: (
    target: ExternalDropTarget,
    element: HTMLElement | null,
  ) => void;
  fallbackPermission?: PermissionSeed | null;
}) {
  const [showChildren, setShowChildren] = useState(true);
  const {
    folder,
    searchTerm,
    showChildFiles = true,
    showChevron = true,
    showCheckbox = true,
  } = props;

  const rowRef = useRef<HTMLDivElement>(null);

  const filteredFiles = folder.files.filter((file: FolderWrapper["files"][0]) =>
    searchTerm.length > 2
      ? file.name.toLowerCase().includes(searchTerm.toLowerCase())
      : true,
  );

  const {
    selectedFiles,
    selectedFolders,
    setSelectedFolders,
    setSelectedFiles,
    indexingEnabled,
  } = useContext(DataRoomContext);

  const prefetchHandlers = usePrefetchDataRoomFolderBrowse(
    props.folder.folder.id,
  );

  const folderPermission = props.folder.folder.permissions?.[0];
  const registrationPermission = useMemo<PermissionSeed | null>(() => {
    const permission = folderPermission ?? props.fallbackPermission ?? null;
    if (!permission) return null;
    return {
      id: permission.id,
      name: permission.name,
      description:
        "description" in permission
          ? (permission.description as string | null | undefined)
          : undefined,
    };
  }, [
    folderPermission?.id,
    folderPermission?.name,
    folderPermission?.description,
    props.fallbackPermission?.id,
    props.fallbackPermission?.name,
    props.fallbackPermission?.description,
  ]);

  useEffect(() => {
    if (!props.registerExternalFolderTarget) return;
    props.registerExternalFolderTarget(
      {
        folderId: props.folder.folder.id,
        folderName: props.folder.folder.name || "Home",
        permission: registrationPermission,
      },
      rowRef.current,
    );
    return () => {
      props.registerExternalFolderTarget?.(
        {
          folderId: props.folder.folder.id,
          folderName: props.folder.folder.name || "Home",
          permission: null,
        },
        null,
      );
    };
  }, [
    props.registerExternalFolderTarget,
    props.folder.folder.id,
    props.folder.folder.name,
    registrationPermission,
  ]);

  function getAllChildFolderIds(folder: FolderWrapper): string[] {
    return [
      folder.folder.id,
      ...folder.folders.flatMap((f) => getAllChildFolderIds(f)),
    ];
  }

  function getAllChildFileIds(folder: FolderWrapper): string[] {
    return [
      ...folder.files.map((f) => f.id),
      ...folder.folders.flatMap((f) => getAllChildFileIds(f)),
    ];
  }

  function toggleFolderSelection() {
    if (selectedFolders.includes(props.folder.folder.id)) {
      let removedFolders = selectedFolders;
      const childFolderIds = getAllChildFolderIds(props.folder);
      for (const folderId of [props.folder.folder.id, ...childFolderIds]) {
        removedFolders = removedFolders.filter((f) => f !== folderId);
      }

      setSelectedFolders(removedFolders);

      let removedFiles = selectedFiles;
      const childFileIds = getAllChildFileIds(props.folder);
      for (const fileId of childFileIds) {
        removedFiles = removedFiles.filter((f) => f !== fileId);
      }
      setSelectedFiles(removedFiles);
    } else {
      const childFolderIds = getAllChildFolderIds(props.folder);
      setSelectedFolders([...selectedFolders, ...childFolderIds]);

      const childFileIds = getAllChildFileIds(props.folder);
      let addedFiles = selectedFiles;
      for (const fileId of childFileIds) {
        if (!selectedFiles.includes(fileId)) {
          addedFiles.push(fileId);
        }
      }
      setSelectedFiles(addedFiles);
    }
  }

  return (
    <div className="">
      <div
        ref={rowRef}
        onClick={() => {
          setShowChildren(!showChildren);
        }}
        onMouseEnter={prefetchHandlers.onMouseEnter}
        onMouseLeave={prefetchHandlers.onMouseLeave}
        onFocus={prefetchHandlers.onFocus}
        className={clsx(
          "items-center hover:bg-gray-100/50 px-2 flex py-2.5 justify-between cursor-pointer data-[external-drop-over=true]:bg-persian-100",
          selectedFolders.includes(props.folder.folder.id)
            ? "bg-persian-100/50"
            : "",
        )}
        style={{ paddingLeft: `${props.depth * 0.8}rem` }}
      >
        <div className="flex items-center gap-x-1">
          {showChevron ? (
            showChildren ? (
              <ChevronDownIcon className="w-4 h-4" />
            ) : (
              <ChevronRightIcon className="w-4 h-4" />
            )
          ) : null}
          {showCheckbox ? (
            <Checkbox
              checked={selectedFolders.includes(props.folder.folder.id)}
              onChange={() => {
                toggleFolderSelection();
              }}
              indeterminate={false}
            />
          ) : null}
          <div className="flex items-center gap-x-2 ml-2">
            {indexingEnabled ? (
              <p className="text-sm text-gray-500">
                {getDataRoomIndex(
                  props.folder.folder.index?.sortIndex,
                  props.folder.folder.parentFolders,
                  "folder",
                )}
              </p>
            ) : null}
            <FolderIcon className="w-5 h-5 text-blue-700/70" />
            <p className="font-semibold mt-1 ml-1 select-none text-gray-800 text-sm">
              {props.folder.folder.name ? props.folder.folder.name : "Home"}
            </p>
          </div>
        </div>
      </div>
      <div>
        {showChildren ? (
          <div>
            {props.folder.folders.map((folder) => (
              <FolderContent
                key={folder.folder.id}
                folder={folder}
                searchTerm={props.searchTerm}
                mode={props.mode}
                depth={props.depth + 1}
                showChevron={props.showChevron}
                showCheckbox={props.showCheckbox}
                showChildFiles={props.showChildFiles}
                registerExternalFolderTarget={
                  props.registerExternalFolderTarget
                }
                fallbackPermission={props.fallbackPermission}
              />
            ))}

            {showChildFiles
              ? filteredFiles.map((file: FolderWrapper["files"][0]) => (
                  <FileRow
                    key={file.id}
                    file={file}
                    depth={props.depth + 1}
                  />
                ))
              : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function FileRow(props: {
  file: FolderWrapper["files"][0];
  depth: number;
}) {
  const { file } = props;
  const history = useHistory();
  const { dealId } = useParams<{ dealId: string }>();
  const { selectedFiles, selectedFolders, setSelectedFiles, indexingEnabled } =
    useContext(DataRoomContext);
  const prefetchHandlers = usePrefetchDataRoomFileShell(file.id);

  function toggleFileSelection() {
    if (selectedFiles.includes(file.id)) {
      setSelectedFiles(selectedFiles.filter((f) => f !== file.id));
    } else {
      setSelectedFiles([...selectedFiles, file.id]);
    }
  }

  return (
    <div
      className={clsx(
        "cursor-pointer flex items-center justify-between px-3 py-2.5 hover:bg-gray-100",
        selectedFiles.includes(file.id) ? "bg-persian-100/50" : "",
      )}
      onMouseEnter={prefetchHandlers.onMouseEnter}
      onMouseLeave={prefetchHandlers.onMouseLeave}
      onFocus={prefetchHandlers.onFocus}
      onClick={() => {
        if (selectedFiles.length + selectedFolders.length > 0) {
          toggleFileSelection();
          return;
        }

        history.push(
          `/deal/${dealId}/documents/folder/${file.folderId}/file/${file.id}`,
        );
      }}
      style={{
        paddingLeft: `${(props.depth + 1) * 0.8}rem`,
      }}
    >
      <div className="flex items-center gap-x-1.5">
        <Checkbox
          checked={selectedFiles.includes(file.id)}
          onChange={() => {
            toggleFileSelection();
          }}
          indeterminate={false}
        />
        <div className="flex items-center gap-x-1.5 ml-1.5">
          {indexingEnabled ? (
            <p className="text-sm text-gray-500">
              {getDataRoomIndex(
                file.index?.sortIndex,
                file.parentFolders ?? [],
                "file",
              )}
            </p>
          ) : null}

          <FileIcon fileType={file.fileType as FileType} />
          <div>
            <p className="text-sm font-semibold text-gray-800 leading-tight">
              {file.name}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
